import React, { useState, useEffect, useRef } from "react";
import Slider from 'react-slick';
import Link from 'next/link';
import { getSizedImageUrl } from '@/utils/Images';
import ProductCard from '@/snippets/globals/layout/ProductCard';
import { newLineToBr } from '@/utils/Helper';
import { useCollection, useCountry, useSettings, useCustomerOrders } from '@backpackjs/storefront';
import OrderRecommendation from "@/snippets/components/OrderRecommendation";
import {FontSelector} from '@/settings/FontSelector';

function Section({ cms, collection }) {
  const { textColor, textColor_m, aboveTheFold = false, hideTextMobile = false, imageWidth = 'lg:w-3/12', productToShow = '4.4'} = cms;
  const siteSettings = useSettings();
  const bwpSettings = siteSettings?.buyWithPrime;
  const { country } = useCountry();
  const isUs = country?.isoCode === 'US';
  let showBWPBadge = false;
  const sliderRef = useRef(null);
  const [mgRight, setMgRight] = useState(0);

  const sliderWidth = imageWidth === 'lg:w-3/12' ? 'lg:w-9/12' : 'lg:w-7/12';

  const { orders } = useCustomerOrders({ perPage: 5, page: 1 });
  const [initialOrders, setInitialOrders] = useState(null);

  let previouslyOrderedCarousel = false;
  const previousOrderLineItems = [];

  if (orders && initialOrders === null) {
    setInitialOrders(orders);
  }

  if (initialOrders) {
    previouslyOrderedCarousel = cms.recommendPreviouslyOrderedProducts && initialOrders.length > 3;

    initialOrders?.forEach((order) => {
      order.lineItems.edges.forEach((lineItem) => {
        const lineItemData = {
          orderId: order.id,
          title: lineItem.node.title
        }

        previousOrderLineItems.push(lineItemData);
      })
    })
  }

  if (bwpSettings?.bwpLocation === 'global' && isUs) {
    showBWPBadge = true;
  }

  if (
    bwpSettings?.bwpLocation === 'local' &&
    bwpSettings?.collectionList?.indexOf(collection?.title) > -1 &&
    isUs
  ) {
    showBWPBadge = true;
  }

  useEffect(() => {
    if (!sliderRef?.current || !cms?.overflowRightEdge) return;
    const windowOffsetRight = (ele) => {
      let rect = ele.getBoundingClientRect ();
      let width = document.body.clientWidth || window.clientWidth;
      return  width - (rect.x + rect.width) > 0 ? width - (rect.x + rect.width) : 0;
    }

    const reachRightEdge = () => {
      if (!sliderRef?.current) return;
      const slider = sliderRef.current;
      const parentEl = slider.closest('.recommendations-right-area');
      if (!parentEl) return;
      const right = -1 * windowOffsetRight(parentEl);
      setMgRight(`${right}px`);
    }
    const debounceFunction = (cb, delay) => {
      let timer;
      return () => {
        if (timer) clearTimeout(timer);
        timer = setTimeout(() => cb(), delay);
      };
    };
    const debounce = debounceFunction(reachRightEdge, 300);
    debounce();
    window.addEventListener('resize', debounce);


    return () => {
      window.removeEventListener('resize', debounce);
    };
  }, [sliderRef?.current, cms?.overflowRightEdge]);

  const getCTAclass = (style) => {
    if (style === 'link') return 'focus-link';
    if (style === 'white-link') return 'focus-link-white';
    return '';
  };

  const buildImage = () => {
    if (cms?.section_image_desktop || cms?.section_image_mobile) {
      return cms?.section_image_link?.url ? (
        <Link href={cms?.section_image_link?.url}>
          <a className="relative cursor-pointer">
            <picture className="relative block h-auto w-full lg:h-full">
              {cms?.section_image_mobile && (
                <source
                  srcSet={getSizedImageUrl(
                    cms?.section_image_desktop.src,
                    '432x@2x'
                  )}
                  media="(min-width: 1024px)"
                  width="864"
                  height="1420"
                />
              )}
              <img
                src={getSizedImageUrl(
                  cms?.section_image_mobile?.src ||
                    cms?.section_image_desktop?.src,
                  '670x405@2x'
                )}
                alt={cms.section_image_alt}
                loading={aboveTheFold ? 'eager' : 'lazy'}
                className="top-0 left-0 w-full object-cover object-center lg:absolute lg:h-full"
                width="1340"
                height="800"
              />
            </picture>

            <div className="absolute bottom-0 left-0 hidden w-full py-4 px-5 sm:py-6 md:py-8 lg:block lg+:px-7">
              {cms?.desktop_image_title && (
                <h3
                  className={`m-0 mb-3 ${cms?.desktopImageTitleFont || 'font-tiempos'} text-h3-m md:text-h4 ${textColor}`}
                >
                  {cms?.desktop_image_title}
                </h3>
              )}
              {cms?.desktop_image_copy && (
                <p className={`mb-2 ${cms?.desktopImageDescriptionFont || 'font-haffer'} ${textColor}`}>{cms?.desktop_image_copy}</p>
              )}
              <span
                className={`${getCTAclass(
                  cms.section_image_cta_style
                )} cursor-pointer text-[14px] leading-[22px] ${cms?.desktopImageDescriptionFont || 'font-haffer'}`}
              >
                {cms.section_image_link.text}
              </span>
            </div>

            <div className="absolute bottom-0 left-0 block w-full py-4 px-5 sm:py-6 md:py-8 lg:hidden lg+:px-7">
              {cms?.mobile_image_title && (
                <h3
                  className={`m-0 mb-3 ${cms?.mobileImageTitleFont || 'font-tiempos'} text-h3-m md:text-h4 ${textColor_m}`}
                >
                  {cms?.mobile_image_title}
                </h3>
              )}
              {cms?.mobile_image_description && (
                <p className={`mb-2 ${cms?.mobileImageDescriptionFont || 'font-haffer'} ${textColor_m}`}>
                  {cms?.mobile_image_description}
                </p>
              )}
              <span className="focus-link cursor-pointer ${cms?.mobileImageDescriptionFont || 'font-haffer'}">
                {cms.section_image_link.text}
              </span>
            </div>
          </a>
        </Link>
      ) : (
        <picture className="relative block h-auto w-full lg:h-full">
          {cms?.section_image_mobile && (
            <source
              srcSet={getSizedImageUrl(
                cms?.section_image_desktop.src,
                '432x@2x'
              )}
              media="(min-width: 1024px)"
              width="864"
              height="1420"
            />
          )}
          <img
            src={getSizedImageUrl(
              cms?.section_image_mobile?.src || cms?.section_image_desktop?.src,
              '670x405@2x'
            )}
            alt={cms.section_image_alt}
            loading={aboveTheFold ? 'eager' : 'lazy'}
            className="top-0 left-0 w-full object-cover object-center lg:absolute lg:h-full"
            width="1340"
            height="800"
          />
          <div className="absolute bottom-0 left-0 hidden w-full py-4 px-5 sm:py-6 md:py-8 lg:block lg+:px-7">
            {cms?.desktop_image_title && (
              <h3
                className={`m-0 mb-3 ${cms?.desktopImageTitleFont || 'font-tiempos'} text-h3-m md:text-h4 ${textColor}`}
              >
                {cms?.desktop_image_title}
              </h3>
            )}
            {cms?.desktop_image_copy && (
              <p className={`mb-0 ${cms?.desktopImageDescriptionFont || 'font-haffer'} ${textColor}`}>{cms?.desktop_image_copy}</p>
            )}
          </div>

          <div className="absolute bottom-0 left-0 block w-full py-4 px-5 sm:py-6 md:py-8 lg:hidden lg+:px-7">
            {cms?.mobile_image_title && (
              <h3
                className={`m-0 mb-3 ${cms?.mobileImageTitleFont || 'font-tiempos'} text-h3-m md:text-h4 ${textColor_m}`}
              >
                {cms?.mobile_image_title}
              </h3>
            )}
            {cms?.mobile_image_description && (
              <p className={`mb-0 ${cms?.mobileImageDescriptionFont || 'font-haffer'} ${textColor_m}`}>
                {cms?.mobile_image_description}
              </p>
            )}
          </div>
        </picture>
      );
    }

    return null;
  };

  const buildText = (floatingCta = false) => {
    return (
      <div className={`recommendations-text relative mx-auto mb-7 flex w-full max-w-[440px] flex-col items-center px-4 text-center md:mb-15 md:px-0 lg:block lg:max-w-full lg:text-left ${hideTextMobile ? 'max-lg:hidden' : ''}`}>
        {cms?.section_title && (
          <h2 className={`text-header mb-5 lg:mb-2 lg:max-w-md xl:max-w-lg ${cms?.titleFont || 'font-tiempos'}`}>
            {cms?.section_title}
          </h2>
        )}
        {cms?.section_description && (
          <p
            className={`mb-5 ${cms?.descriptionFont || 'font-haffer'} text-h6-m lg:mb-4 lg:max-w-sm lg:text-h6 xl:max-w-lg`}
            dangerouslySetInnerHTML={{
              __html: newLineToBr(cms?.section_description),
            }}
          />
        )}
        {cms?.section_cta?.text && cms?.section_cta?.url && (
          <div
            className={`${cms?.descriptionFont || 'font-haffer'} ${
              floatingCta
                ? 'lg:absolute lg:right-[7%] lg:bottom-0 '
                : 'relative'
            }`}
          >
            <Link href={cms?.section_cta.url}>
              <a
                className="focus-link"
                target={cms?.section_cta.newTab ? '_blank' : null}
              >
                {cms?.section_cta.text}
              </a>
            </Link>
          </div>
        )}
      </div>
    );
  };

  if (cms.recommendPreviouslyOrderedProducts && orders && orders.length === 0) { return; }

  return (
    <section
      id={`${Section.Schema.key}-${cms.id || cms.cmsId || cms.tinaId}`}
      key={cms?.cmsId}
      data-comp={Section.key}
    >
      <style>
        {`#${Section.Schema.key}-${cms.id || cms.cmsId || cms.tinaId} {
            margin-top: ${cms.mobileSpacing}px;
          }
          @media(min-width: 1024px) {
            #${Section.Schema.key}-${cms.id || cms.cmsId || cms.tinaId} {
              margin-top: ${cms.desktopSpacing}px;
            }
          }`}
      </style>
      {cms?.uniqueId && (
        <div id={cms.uniqueId} />
      )}
      {cms?.section_heading && (
        <h2 className={`mx-4 lg:mx-0 mt-0 mb-4 text-h3-m md:text-h4 lg:mb-6 ${cms?.sectionHeadingFont || 'font-haffer'}`}>
          {cms?.section_heading}
        </h2>
      )}
      <div className="flex flex-col items-center px-0 md-:px-12 lg:flex-row lg:items-stretch">
        <div className={`recommendations-left-area mb-6 w-full shrink-0 md:mb-7 lg:mb-0 ${imageWidth} ${hideTextMobile ? 'max-lg:hidden' : ''}`}>
          {buildImage() ? buildImage() : buildText()}
        </div>
        <div className={`recommendations-right-area w-full lg:pl-7 ${sliderWidth}`}>
          {buildImage() && buildText(true)}
          <div style={{
              marginRight: mgRight
            }}
            ref={sliderRef}
          >
            {previouslyOrderedCarousel || !cms.recommendPreviouslyOrderedProducts && cms?.product_list?.length > 3 ? (
              <Slider
                slidesToShow={Number(productToShow)}
                infinite
                arrows
                draggable
                slidesToScroll={1}
                responsive={[
                  {
                    breakpoint: 1500,
                    settings: {
                      slidesToShow: Number(productToShow) - 1,
                      slidesToScroll: 1,
                    },
                  },
                  {
                    breakpoint: 1100,
                    settings: {
                      slidesToShow: Number(productToShow) - 2,
                      slidesToScroll: 1,
                    },
                  },
                  {
                    breakpoint: 1024,
                    settings: {
                      slidesToShow: Number(productToShow) - 1,
                      slidesToScroll: 1,
                      infinite: false,
                      arrows: false,
                    },
                  },
                  {
                    breakpoint: 450,
                    settings: {
                      slidesToShow: Number(productToShow) - 3 > 1 ? Number(productToShow) - 3 : 1.15,
                      slidesToScroll: 1,
                      infinite: false,
                      arrows: false,
                    },
                  },
                ]}
                className="product-recommendations-slider"
              >
                {previouslyOrderedCarousel && previousOrderLineItems.map((lineItem, index) => {
                  return (
                    <OrderRecommendation
                      key={index}
                      orderId={lineItem.orderId}
                      title={lineItem.title}
                      showBWPBadge={showBWPBadge}
                      aboveTheFold={aboveTheFold}
                      index={index}
                    />
                  )
                })}

                {!previouslyOrderedCarousel && (cms?.product_list || []).map((productItem, index) => {
                  return productItem.product ? (
                    <ProductCard
                      handle={productItem.product.handle}
                      showReviews={false}
                      quickViewButton="bottom"
                      key={index}
                      showBWPBadge={showBWPBadge}
                      belowFold={!aboveTheFold}
                      isSection
                    />
                  ) : null;
                })}
              </Slider>
            ) : (
              <div className="flex gap-5">
                {cms.recommendPreviouslyOrderedProducts && initialOrders && initialOrders.length && previousOrderLineItems.map((lineItem, index) => {
                  if (index >= 3) { return; }
                  return (
                    <div className="max-w-xs flex-1">
                      <OrderRecommendation
                        orderId={lineItem.orderId}
                        title={lineItem.title}
                        showBWPBadge={showBWPBadge}
                        aboveTheFold={aboveTheFold}
                        index={index}
                      />
                    </div>
                  );
                })}

                {!cms.recommendPreviouslyOrderedProducts && cms?.product_list.map((productItem, index) => {
                  return productItem.product ? (
                    <div className="max-w-xs flex-1">
                      <ProductCard
                        handle={productItem.product.handle}
                        showReviews={false}
                        quickViewButton="bottom"
                        key={index}
                        showBWPBadge={showBWPBadge}
                        belowFold={!aboveTheFold}
                        isSection
                      />
                    </div>
                  ) : null;
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

Section.displayName = 'Product Recommendations';

Section.Schema = {
  category: 'Modules',
  label: Section.displayName,
  key: 'productRecommendations',
  fields: [
    {
      component: 'toggle',
      name: 'aboveTheFold',
      label: 'Above the fold',
      toggleLabels: {
        true: 'Yes',
        false: 'No',
      },
      defaultValue: false,
    },
    {
      name: 'section_heading',
      label: 'Heading',
      component: 'text',
    },
    {
      name: 'sectionHeadingFont',
      label: 'Heading Font',
      component: 'select',
      options: FontSelector,
      defaultValue: 'font-tiempos',
      description: 'Select the font for the section heading text',
    },
    {
      name: 'uniqueId',
      label: 'Unique ID',
      component: 'text',
      description: 'use for anchor links',
    },
    {
      component: 'number',
      name: 'mobileSpacing',
      label: 'Section Top Margin - Mobile',
      defaultValue: 60,
    },
    {
      component: 'number',
      name: 'desktopSpacing',
      label: 'Section Top Margin - Desktop',
      defaultValue: 80,
    },
    {
      component: 'text',
      label: 'Section Title',
      name: 'section_title',
    },
    {
      component: 'text',
      label: 'Section Title Font',
      name: 'titleFont',
      component: 'select',
      options: FontSelector,
      defaultValue: 'font-tiempos',
      description: 'Select the font for the section title text',
    },
    {
      component: 'textarea',
      label: 'Section Description',
      name: 'section_description',
    },
    {
      component: 'text',
      label: 'Section Description Font',
      name: 'descriptionFont',
      component: 'select',
      options: FontSelector,
      defaultValue: 'font-haffer',
      description: 'Select the font for the section description text',
    },
    {
      component: 'link',
      label: 'Section CTA',
      name: 'section_cta',
    },
    {
      component: 'image',
      label: 'Section Image Desktop (optional)',
      name: 'section_image_desktop',
      description: 'Recommended size: 864px x 1420px (Height can be variable)',
    },
    {
      component: 'select',
      name: 'imageWidth',
      label: 'image Container Width',
      options: [
        { label: 'small', value: 'lg:w-3/12' },
        { label: 'Large', value: 'lg:w-5/12' },
      ],
      defaultValue: 'lg:w-3/12',
    },
    {
      component: 'image',
      label: 'Section Image Mobile (optional)',
      name: 'section_image_mobile',
      description: 'Recommended size: 1340px x 800px',
    },
    {
      component: 'text',
      label: 'Section Image Alt',
      name: 'section_image_alt',
      description: 'For ADA. Add description about image',
    },
    {
      component: 'link',
      label: 'Section Image Link',
      name: 'section_image_link',
      description:
        'Optional. Applies to desktop and mobile image. If link and text are not populated then the link will not show',
    },
    {
      name: 'section_image_section_image_',
      label: 'CTA Link Style',
      component: 'select',
      options: [
        { label: 'Focus Link', value: 'link' },
        { label: 'Focus Link White', value: 'white-link' },
      ],
      defaultValue: 'link',
    },
    {
      component: 'text',
      label: 'Desktop Image Title',
      name: 'desktop_image_title',
    },
    {
      component: 'text',
      label: 'Desktop Image Title Font',
      name: 'desktopImageTitleFont',
      component: 'select',
      options: FontSelector,
      defaultValue: 'font-tiempos',
      description: 'Select the font for the desktop image title text',
    },
    {
      component: 'text',
      label: 'Desktop Image Description',
      name: 'desktop_image_copy',
    },
    {
      component: 'text',
      label: 'Desktop Image Description Font',
      name: 'desktopImageDescriptionFont',
      component: 'select',
      options: FontSelector,
      defaultValue: 'font-haffer',
      description: 'Select the font for the desktop image description text',
    },
    {
      component: 'select',
      name: 'textColor',
      label: 'Text Color (Desktop)',
      options: [
        { label: 'Light', value: 'text-white' },
        { label: 'Dark', value: 'text-black' },
      ],
      defaultValue: 'text-black',
    },
    {
      component: 'toggle',
      name: 'hideTextMobile',
      label: 'hide Image & Section Texts (Mobile)',
      toggleLabels: {
        true: 'Yes',
        false: 'No',
      },
      defaultValue: false,
    },
    {
      component: 'text',
      label: 'Mobile Image Title',
      name: 'mobile_image_title',
    },
    {
      component: 'text',
      label: 'Mobile Image Title Font',
      name: 'mobileImageTitleFont',
      component: 'select',
      options: FontSelector,
      defaultValue: 'font-tiempos',
      description: 'Select the font for the mobile image title text',
    },
    {
      component: 'text',
      label: 'Mobile Image Description',
      name: 'mobile_image_description',
    },
    {
      component: 'text',
      label: 'Mobile Image Description Font',
      name: 'mobileImageDescriptionFont',
      component: 'select',
      options: FontSelector,
      defaultValue: 'font-haffer',
      description: 'Select the font for the mobile image description text',
    },
    {
      component: 'select',
      name: 'textColor_m',
      label: 'Text Color (Mobile)',
      options: [
        { label: 'Light', value: 'text-white' },
        { label: 'Dark', value: 'text-black' },
      ],
      defaultValue: 'text-black',
    },
    {
      component: 'select',
      name: 'productToShow',
      label: 'number Product per Slide (Desktop)',
      options: [
        { label: '3', value: '3.3' },
        { label: '4', value: '4.4' },
        { label: '5', value: '5.4' },
      ],
      defaultValue: '4.4',
    },
    {
      component: 'toggle',
      name: 'overflowRightEdge',
      label: 'Slider overflow to right edge',
      toggleLabels: {
        true: 'Yes',
        false: 'No',
      },
      defaultValue: false,
    },
    {
      component: 'group-list',
      label: 'Product List',
      name: 'product_list',
      description: 'Recommended to have at least 5 products populated.',
      fields: [
        {
          component: 'productSearch',
          label: 'Product',
          name: 'product',
        },
      ],
    },
    {
      component: 'toggle',
      name: 'recommendPreviouslyOrderedProducts',
      label: 'Recommend previously ordered products',
      description: 'Note: the section will not show if the user has no orders',
    },
  ],
};

export const ProductRecommendations = Section;